import React from 'react';
import styles from '../CanvassInfo.module.css';
import reportStyles from './ReportSection.module.css';
import cbStyles from './ComprehensiveBusinessReport.module.css';

const STATUS_LABELS = {
  not_started: 'Not started',
  sources_frozen: 'Sources frozen',
  hitl_analysis: 'In analysis',
  qa_review: 'QA review',
  approved: 'Approved',
  shared: 'Shared',
  feedback_captured: 'Feedback captured',
  learning_routed: 'Learning routed',
};

const OBJECTIVE_STATUS_CLASS = {
  answered: reportStyles.priorityLow,
  partially_answered: reportStyles.priorityMedium,
  insufficient_evidence: reportStyles.priorityHigh,
  not_explored: reportStyles.priorityHigh,
};

const OBJECTIVE_STATUS_LABELS = {
  answered: 'Answered',
  partially_answered: 'Partially answered',
  insufficient_evidence: 'Insufficient evidence',
  not_explored: 'Not explored',
};

const SectionHeader = ({ children }) => (
  <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd}`}>
    {children}
  </div>
);

const SubHeader = ({ children }) => (
  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div className={cbStyles.statRow}>
    <div className={reportStyles.listKey}>{label}:</div>
    <div className={reportStyles.listValue}>{value ?? '—'}</div>
  </div>
);

const Quote = ({ quote }) => (
  <div className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
    <div className={reportStyles.interviewQuote}>“{quote?.text || '—'}”</div>
    {quote?.sessionId && (
      <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadSm}`} style={{ marginTop: 6, fontSize: 12 }}>
        Interview {quote.sessionId}
        {quote?.qualifier ? ` · ${quote.qualifier}` : ''}
      </div>
    )}
  </div>
);

const QuestionList = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className={cbStyles.emotionalBeatsGrid}>
      {items.map((item, i) => (
        <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
          <div className={cbStyles.beatStage}>
            <span className={reportStyles.pillStrength} style={{ marginRight: 8 }}>{i + 1}</span>
            {item?.question || '—'}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function ComprehensiveReportV2View({ data, performance }) {
  if (!data || typeof data !== 'object') return null;

  const header = data.header || {};
  const statusLabel = STATUS_LABELS[header.status] || header.status || '—';
  const meta = data.reportMetadata || data.reportMeta || {};

  return (
    <section>
      {/* Header */}
      <div className={styles.section}>
        <SectionHeader>Comprehensive Report</SectionHeader>
        <div className={cbStyles.statsGrid}>
          <div className={cbStyles.statRow}>
            <div className={reportStyles.listKey}>Canvass:</div>
            <div className={reportStyles.listValue}>{header.canvassName || '—'}</div>
          </div>
          <div className={cbStyles.statRow}>
            <div className={reportStyles.listKey}>Organisation:</div>
            <div className={reportStyles.listValue}>{header.organisation || '—'}</div>
          </div>
          <div className={cbStyles.statRow}>
            <div className={reportStyles.listKey}>Date:</div>
            <div className={reportStyles.listValue}>{header.date || '—'}</div>
          </div>
          {/* <div className={cbStyles.statRow}>
            <div className={reportStyles.listKey}>Status:</div>
            <div className={reportStyles.listValue}>
              <span className={`${reportStyles.pillStrength} ${reportStyles.priorityMedium}`}>{statusLabel}</span>
            </div>
          </div> */}
        </div>
        {/* {header.distributionBanner && (
          <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadSm}`} style={{ marginTop: 8 }}>
            {header.distributionBanner}
          </div>
        )} */}
      </div>
      <div className={cbStyles.sectionHeaderPadSm}></div>

      {/* Analyzed Interviews */}
      {(meta.dataSummary || performance) && (
        <div className={styles.section}>
          <SectionHeader>Analyzed Interviews</SectionHeader>
          <div className={`${cbStyles.statsGrid} ${cbStyles.sectionHeaderPadMd}`}>
            {meta.dataSummary?.interviewsProcessed !== undefined && (
              <Stat
                label="Interviews Processed"
                value={meta.dataSummary.interviewsProcessed.toLocaleString()}
              />
            )}
            {performance?.completedVoices !== undefined && (
              <Stat
                label="Completed Voices"
                value={performance.completedVoices.toLocaleString()}
              />
            )}
            {performance?.abandonedVoices !== undefined && (
              <Stat
                label="Abandoned Interviews"
                value={performance.abandonedVoices.toLocaleString()}
              />
            )}
            {(performance?.completionRate !== undefined || meta.dataSummary?.completionRate !== undefined) && (
              <Stat
                label="Completion Rate"
                value={`${(performance?.completionRate ?? meta.dataSummary?.completionRate ?? 0).toFixed(1)}%`}
              />
            )}
          </div>
        </div>
      )}

      {/* Bottom Line */}
      {data.bottomLine && (
        <>
          <div className={styles.section}>
            <SectionHeader>Bottom Line</SectionHeader>
            <div className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>{data.bottomLine}</div>
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>
        </>
      )}

      {/* The Results */}
      {Array.isArray(data.theResults) && data.theResults.length > 0 && (
        <>
          <div className={styles.section}>
            <SectionHeader>The Results</SectionHeader>
            <div className={cbStyles.emotionalBeatsGrid}>
              {data.theResults.map((result, i) => (
                <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                  <div className={cbStyles.beatStage}>
                    <span className={reportStyles.pillStrength} style={{ marginRight: 8 }}>Signal {result?.signalNumber ?? i + 1}</span>
                    {result?.claim || '—'}
                  </div>
                  {Array.isArray(result?.qualifiers) && result.qualifiers.length > 0 && (
                    <div className={cbStyles.emotionsRow}>
                      {result.qualifiers.map((q, j) => (
                        <span key={j} className={reportStyles.pill}>{String(q)}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>
        </>
      )}

      {/* Central Finding */}
      {data.centralFinding && Array.isArray(data.centralFinding.paragraphs) && data.centralFinding.paragraphs.length > 0 && (
        <>
          <div className={styles.section}>
            <SectionHeader>Central Finding</SectionHeader>
            {data.centralFinding.paragraphs.map((paragraph, i) => (
              <div key={i} className={`${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                {paragraph}
              </div>
            ))}
          </div>
        </>
      )}

      {/* What Participants Told Us */}
      {Array.isArray(data.whatParticipantsToldUs) && data.whatParticipantsToldUs.length > 0 && (
        <>
          <div className={styles.section}>
            <SectionHeader>What Participants Told Us</SectionHeader>
            <div className={cbStyles.emotionalBeatsGrid}>
              {data.whatParticipantsToldUs.map((finding, i) => (
                <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                  <div className={cbStyles.beatStage}>{finding?.findingTitle || '—'}</div>
                  {finding?.narrative && (
                    <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>{finding.narrative}</div>
                  )}
                  {Array.isArray(finding?.quotes) && finding.quotes.length > 0 && (
                    <div className={cbStyles.emotionalBeatsGrid}>
                      {finding.quotes.map((quote, j) => (
                        <Quote key={j} quote={quote} />
                      ))}
                    </div>
                  )}
                  {finding?.doingDifferently && (
                    <div className={reportStyles.mb8}>
                      <SubHeader>Doing Differently</SubHeader>
                      <div>{finding.doingDifferently}</div>
                    </div>
                  )}
                  {finding?.wouldStrengthen && (
                    <div className={reportStyles.mb8}>
                      <SubHeader>Would Strengthen</SubHeader>
                      <div>{finding.wouldStrengthen}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>
        </>
      )}

      {/* Answers to the Brief */}
      {Array.isArray(data.answersToTheBrief) && data.answersToTheBrief.length > 0 && (
        <>
          <div className={styles.section}>
            <SectionHeader>Answers to the Brief</SectionHeader>
            <div className={cbStyles.emotionalBeatsGrid}>
              {data.answersToTheBrief.map((row, i) => {
                const statusClass = OBJECTIVE_STATUS_CLASS[row?.status] || reportStyles.priorityMedium;
                const statusLabel = OBJECTIVE_STATUS_LABELS[row?.status] || row?.status || '—';
                return (
                  <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                    <div className={cbStyles.beatStage}>
                      <span className={reportStyles.listKey}>{row?.objectiveId || '—'}</span>
                      <span className={`${reportStyles.pillStrength} ${statusClass}`}>{statusLabel}</span>
                    </div>
                    {row?.objectiveText && (
                      <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>{row.objectiveText}</div>
                    )}
                    {row?.summary && (
                      <div className={reportStyles.textLeft}>{row.summary}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>
        </>
      )}

      {/* The Opportunity */}
      {Array.isArray(data.theOpportunity) && data.theOpportunity.length > 0 && (
        <>
          <div className={styles.section}>
            <SectionHeader>The Opportunity</SectionHeader>
            <QuestionList items={data.theOpportunity} />
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>
        </>
      )}

      {/* What to Learn Next */}
      {Array.isArray(data.whatToLearnNext) && data.whatToLearnNext.length > 0 && (
        <>
          <div className={styles.section}>
            <SectionHeader>What to Learn Next</SectionHeader>
            <QuestionList items={data.whatToLearnNext} />
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>
        </>
      )}

      {/* Evidence & Limitations */}
      {data.evidenceAndLimitations && typeof data.evidenceAndLimitations === 'object' && (
        <>
        <div className={styles.section}>
          <SectionHeader>Evidence &amp; Limitations</SectionHeader>
          <div className={cbStyles.statsGrid}>
            {data.evidenceAndLimitations.participation && (
              <div className={cbStyles.statRow}>
                <div className={reportStyles.listKey}>Participation:</div>
                <div className={`${reportStyles.listValue} ${cbStyles.statValueRight}`}>{data.evidenceAndLimitations.participation}</div>
              </div>
            )}
            {data.evidenceAndLimitations.audience && (
              <div className={cbStyles.statRow}>
                <div className={reportStyles.listKey}>Audience:</div>
                <div className={`${reportStyles.listValue} ${cbStyles.statValueRight}`}>{data.evidenceAndLimitations.audience}</div>
              </div>
            )}
            {data.evidenceAndLimitations.recruitment && (
              <div className={cbStyles.statRow}>
                <div className={reportStyles.listKey}>Recruitment:</div>
                <div className={`${reportStyles.listValue} ${cbStyles.statValueRight}`}>{data.evidenceAndLimitations.recruitment}</div>
              </div>
            )}
            {data.evidenceAndLimitations.interviewDepth && (
              <div className={cbStyles.statRow}>
                <div className={reportStyles.listKey}>Depth:</div>
                <div className={`${reportStyles.listValue} ${cbStyles.statValueRight}`}>{data.evidenceAndLimitations.interviewDepth}</div>
              </div>
            )}
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>
          

          {Array.isArray(data.evidenceAndLimitations.cannotEstablish) && data.evidenceAndLimitations.cannotEstablish.length > 0 && (
            <>
              <div className={reportStyles.mb8}>
                <SectionHeader>Cannot Establish</SectionHeader>
                <ul className={cbStyles.list}>
                  {data.evidenceAndLimitations.cannotEstablish.map((item, i) => (
                    <li key={i} className={cbStyles.listItem}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className={cbStyles.sectionHeaderPadSm}></div>
            </>
          )}

          {data.evidenceAndLimitations.augustComparison && (
            <div className={reportStyles.mb8}>
              <SubHeader>August Comparison</SubHeader>
              <div className={reportStyles.textLeft}>{data.evidenceAndLimitations.augustComparison}</div>
            </div>
          )}
        </div>
        <div className={cbStyles.sectionHeaderPadSm}></div>
        </>
      )}

    </section>
  );
}
